import React, { useState, useRef } from 'react';
import { Camera, Upload, Trash2, Sparkles } from 'lucide-react';
import { compressImage } from '../../utils/imageCompressor';

interface PhotoUploaderProps {
  photos: string[];
  onChange: (photos: string[]) => void;
  zoneName?: string;
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({ photos, onChange, zoneName }) => {
  const [compressing, setCompressing] = useState(false);
  const [lastStats, setLastStats] = useState<{ orig: number; comp: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setCompressing(true);
    try {
      const newPhotos: string[] = [...photos];
      let totalOrig = 0;
      let totalComp = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await compressImage(file, {
          watermarkText: zoneName
            ? `ЖК «Северная Звезда» • ${zoneName} • ${new Date().toLocaleDateString('ru-RU')} ${new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
            : undefined
        });
        newPhotos.push(res.dataUrl);
        totalOrig += res.originalSize;
        totalComp += res.compressedSize;
      }

      setLastStats({ orig: totalOrig, comp: totalComp });
      onChange(newPhotos);
    } catch (err) {
      console.error('Ошибка сжатия изображения:', err);
      alert('Ошибка при сжатии фото. Попробуйте еще раз.');
    } finally {
      setCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    const next = photos.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Camera className="w-4 h-4 text-amber-600" />
          <span>Фотофиксация объемов / скрытых трасс</span>
        </label>
        {lastStats && (
          <span className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-200 flex items-center gap-1 font-semibold">
            <Sparkles className="w-3 h-3" />
            Сжато: {(lastStats.orig / 1024 / 1024).toFixed(1)} МБ ➔ {(lastStats.comp / 1024).toFixed(0)} КБ (WebP)
          </span>
        )}
      </div>

      {/* Upload Buttons */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          type="button"
          disabled={compressing}
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100 active:scale-[0.99] border border-slate-200 p-3.5 rounded-xl text-sm font-semibold text-slate-800 transition shadow-sm"
        >
          {compressing ? (
            <span className="text-amber-700 flex items-center gap-2 animate-pulse font-bold">
              <Upload className="w-4 h-4 animate-bounce" />
              Сжатие Canvas (WebP)...
            </span>
          ) : (
            <>
              <Camera className="w-4 h-4 text-blue-600" />
              <span>Сделать фото с камеры / Загрузить файл</span>
            </>
          )}
        </button>
      </div>

      {/* Photo Previews */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1">
          {photos.map((src, index) => (
            <div key={index} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-100 aspect-video shadow-sm">
              <img src={src} alt={`Фото ${index + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1.5 right-1.5 p-1.5 bg-rose-600 text-white rounded-lg opacity-90 hover:opacity-100 hover:bg-rose-700 transition"
                title="Удалить фото"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <span className="absolute bottom-1 left-1 bg-slate-900/80 text-[10px] text-white px-2 py-0.5 rounded-md font-semibold">
                #{index + 1}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
